import isHttpsUrl from "./isHttpsUrl";

/**
 * Transforms raw `GET /stores/{storeId}/checklist/today` and
 * `GET /stores/{storeId}/checklist/team-progress` responses (Staff Ops API
 * doc, #sm-today / #sm-team) into what ChecklistProgressList + the summary
 * cards need. Pure functions, no fetching — src/helpers/checklistApi.js
 * does that.
 */

// Role/worker progress status -> V2Badge props. Deliberately reuses the
// matrix's four color buckets (done/flagged/pending/not_submitted) with
// different labels, rather than inventing a second color vocabulary.
const PROGRESS_STATUS_BADGE = {
  not_started: { status: "pending", label: "Not started" },
  in_progress: { status: "not_submitted", label: "In progress" },
  completed: { status: "done", label: "Completed" },
  critical: { status: "flagged", label: "Critical" },
};

export function getProgressBadgeProps(progressStatus) {
  return PROGRESS_STATUS_BADGE[progressStatus] || PROGRESS_STATUS_BADGE.not_started;
}

// Workers aren't given a `status` enum directly (only completed/total/
// locked) — derive one using the same vocabulary as roles/sections. There's
// no per-worker "critical" signal at this level, so this only ever derives
// the other three.
function deriveWorkerStatus(worker) {
  if (worker.total > 0 && worker.completed >= worker.total) return "completed";
  if (worker.completed > 0) return "in_progress";
  return "not_started";
}

function getItemStatus(item) {
  if (item.response == null) return "pending";
  if (item.response === "no" || item.severity === "critical") return "flagged";
  return "done";
}

/**
 * Builds the single-store { stores, items, sectionSummary } shape that
 * ChecklistMatrixTable expects, from `team-progress` plus one
 * `checklist/roles/{roleId}` response per role. The role-detail response
 * carries no section, so rows are grouped by the item's `section` when the
 * backend sends one and by role otherwise.
 */
export function adaptStoreManagerMatrix({ storeId, storeName, managerName, teamProgress, roleDetails }) {
  const stores = [{ id: storeId, nameLines: [storeName], managerName: managerName || "—" }];

  // ChecklistMatrixTable expects items already grouped by section.
  const itemsBySection = new Map();
  (teamProgress || []).forEach((role, roleIndex) => {
    (roleDetails[roleIndex]?.items || []).forEach((item, itemIndex) => {
      const section = item.section || role.roleName;
      if (!itemsBySection.has(section)) itemsBySection.set(section, []);
      itemsBySection.get(section).push({
        id: `${role.roleId}-${item.id ?? itemIndex}`,
        title: item.text,
        roleName: role.roleName,
        section,
        cellsByStore: {
          [storeId]: {
            status: item.status || getItemStatus(item),
            respondedBy: item.respondedBy ?? null,
            // Photos the worker attached; only safe https links are kept.
            imageUrls: (Array.isArray(item.imageUrls) ? item.imageUrls : []).filter(isHttpsUrl),
          },
        },
      });
    });
  });

  const items = [...itemsBySection.values()].flat();
  const sectionSummary = [...itemsBySection].map(([name, sectionItems]) => ({ name, count: sectionItems.length }));

  const counts = { done: 0, flagged: 0, pending: 0 };
  items.forEach((item) => {
    counts[item.cellsByStore[storeId].status] += 1;
  });
  const overallPercent = items.length > 0 ? Math.round((counts.done / items.length) * 100) : 0;

  return { stores, items, sectionSummary, summary: { ...counts, overallPercent } };
}

export function adaptStoreManagerData(todayData, teamProgressData) {
  const roles = (teamProgressData || []).map((role) => ({
    id: role.roleId,
    label: role.roleName,
    completed: role.completed,
    total: role.total,
    status: role.status,
  }));

  const workers = (todayData?.workers || []).map((worker) => ({
    id: worker.staffId,
    label: worker.name,
    sublabel: worker.username,
    completed: worker.completed,
    total: worker.total,
    status: deriveWorkerStatus(worker),
  }));

  const totalCompleted = roles.reduce((sum, role) => sum + role.completed, 0);
  const totalItems = roles.reduce((sum, role) => sum + role.total, 0);
  const criticalRoleCount = roles.filter((role) => role.status === "critical").length;
  const overallPercent = totalItems > 0 ? Math.round((totalCompleted / totalItems) * 100) : 0;

  return {
    roles,
    workers,
    summary: {
      completed: totalCompleted,
      remaining: totalItems - totalCompleted,
      criticalRoleCount,
      overallPercent,
    },
  };
}
